import { useAtomValue, useSetAtom } from 'jotai';
import { useSelector } from 'react-redux';

import type { User } from '@/@types/global';
import { useCreateInitialFindings } from '@/modules/annotator/helpers';
import { computeImagesRefetchInterval } from '@/modules/annotator/utils';
import { useGetVideaImage } from '@/react-query/useGetVideaImage';
import { useGetVideaPatient } from '@/react-query/useGetVideaPatient';
import { useLatestImagesV2ToV1 } from '@/react-query/useLatestImagesv2';
import { usePreferences } from '@/react-query/usePreferences';
import { useVisitSummaries } from '@/react-query/useVisitSummaries';

import {
  selectedUnifiedIdAtom,
  selectedVisitAtom,
} from '../appHeader/imageViewerMenu/atoms';

interface ExternalPatientArgs {
  externalPatientId: string | null;
  externalImageId: string | null;
  enabled: boolean;
}

export const useExternalPatientData = ({
  externalPatientId,
  externalImageId,
  enabled,
}: ExternalPatientArgs) => {
  const setSelectedVisit = useSetAtom(selectedVisitAtom);
  const setSelectedUnifiedId = useSetAtom(selectedUnifiedIdAtom);
  const selectedVisit = useAtomValue(selectedVisitAtom);

  const { createInitialFindings } = useCreateInitialFindings();
  const { data: prefs } = usePreferences();

  const reduxLogin = useSelector(
    (state: { login?: { user?: User } }) => state.login,
  );
  const organizationId = reduxLogin?.user?.primaryOrganizationId ?? null;
  const practiceId = reduxLogin?.user?.primaryPracticeId ?? null;

  // Step 1: Get Videa patient ID from external patient ID
  const {
    data: patientIdData,
    error: patientIdError,
    isLoading: isLoadingPatientId,
  } = useGetVideaPatient({
    practicePatientId: externalPatientId,
    primaryOrganizationId: organizationId,
    primaryPracticeId: practiceId,
    enabled,
  });

  // Step 2: Get Videa image ID from external image ID
  const {
    data: imageIdData,
    error: imageIdError,
    isLoading: isLoadingImageId,
  } = useGetVideaImage({
    practiceImageId: externalImageId,
    practicePatientId: externalPatientId,
    primaryOrganizationId: organizationId,
    primaryPracticeId: practiceId,
    enabled,
    onSuccess: (data) => {
      if (data.estimatedVisitId && data.unifiedAppointmentId) {
        setSelectedVisit(String(data.estimatedVisitId));
        setSelectedUnifiedId(String(data.unifiedAppointmentId));
      }
    },
  });

  // Step 3: Get visit summary from estimated visit ID
  const visitSummariesResults = useVisitSummaries({
    visits: enabled
      ? [{ estimatedVisitId: selectedVisit ? parseInt(selectedVisit) : null }]
      : [],
  });
  const {
    data: visitSummaries,
    error: visitSummariesError,
    isLoading: isLoadingVisitSummaries,
  } = visitSummariesResults[0] ?? {
    data: undefined,
    error: undefined,
    isLoading: false,
  };

  // Step 4: Get latest images from estimated visit ID
  const {
    data: latestImages,
    error: latestImagesError,
    isLoading: isLoadingLatestImages,
  } = useLatestImagesV2ToV1({
    videaPatientId: patientIdData?.patientId ?? '',
    externalPatientId: externalPatientId ?? '',
    options: {
      enabled,
      // The createInitialFindings helper processes each image and adds it to the redux state
      onSuccess: (data) =>
        createInitialFindings(data, patientIdData?.patientId ?? ''),
      refetchInterval: computeImagesRefetchInterval(prefs, false, false),
    },
  });

  return {
    patientIdData,
    imageIdData,
    isLoadingPatientData: isLoadingPatientId || isLoadingImageId,
    isErrorPatientData: patientIdError || imageIdError,
    visitSummaries,
    latestImages,
    isLoadingVisitAndImageData:
      isLoadingVisitSummaries || isLoadingLatestImages,
    isErrorVisitAndImageData: visitSummariesError || latestImagesError,
    isSuccess:
      !!patientIdData && !!imageIdData && !!visitSummaries && !!latestImages,
  };
};
